import { useTheme } from '@fluentui/react/lib/Theme';
import AppContext from 'App/AppContext';
import { IPIconButton, IPLabel, IPText, IPTooltipHost } from 'baseComponents';
import { IPPersona, IPPersonaSize } from 'baseComponents/Persona/IPPersona';
import { IPStack } from 'baseComponents/Stack/IPStack';
import AuthSchemaPicker from 'components/Pickers/AuthSchemaPicker';
import UserPicker from 'components/Pickers/UserPicker';
import { canUpdateTaskField, hasTaskUpdatePermission, TaskFieldTypes } from 'components/Tasks/TaskAuthHelper';
import { globalStackTokensGapExtraSmall, globalStackTokensGapSmall } from 'globalStyles';
import Task from 'models/tasks/task';
import User from 'models/user';
import { Fragment, useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { apiGetAuthSchemaLines } from 'services/Api/authService';
import { apiRequest } from 'services/Auth/authConfig';
import { canShowTaskSchema, getTaskSchemaName } from './taskDetailRules';

export interface ITaskAssignmentProps {
  orgTask: Task;
  task: Task;
  disabled?: boolean;
  canUpdate: boolean | undefined;
  isReadonlyPermissions?: boolean;
  isReadonlyAssignee?: boolean;
  onUpdate: (newTask: Task) => void;
  getErrorMessage?: (field: string) => string | undefined;
  // show only the assignee, without the shared-with picker
  hidePermissions?: boolean;
  // show only the shared-with picker, without the assignee
  hideAssignee?: boolean;
}

export const TaskAssignment = (props: ITaskAssignmentProps) => {
  const appContext = useContext(AppContext);
  const theme = useTheme();
  const { t } = useTranslation(['task', 'control', 'translation', 'dateTimeComponent', 'adminAuth']);
  const [authSchemaId, setAuthSchemaId] = useState<number | undefined>(undefined);
  const [authSchemaUsersLoading, setAuthSchemaUsersLoading] = useState<boolean>(false);
  const [authSchemaUsers, setAuthSchemaUsers] = useState<User[]>([]);
  const [canUpdate] = useState<boolean>(props.canUpdate ?? hasTaskUpdatePermission(props.orgTask, appContext));
  const isReadonlyPermissions =
    props.disabled ||
    props.isReadonlyPermissions ||
    !canUpdateTaskField(props.orgTask, canUpdate, TaskFieldTypes.Permissions, appContext);
  const isReadonlyAssignee =
    props.disabled ||
    props.isReadonlyAssignee ||
    !canUpdateTaskField(props.orgTask, canUpdate, TaskFieldTypes.Assignee, appContext);
  const [isReadonlyAssignToMe, setIsReadonlyAssignToMe] = useState<boolean>(true);
  const rowTokens = appContext.isMobileView ? globalStackTokensGapExtraSmall : globalStackTokensGapSmall;

  //
  // Effects
  //
  useEffect(() => {
    if (props.task.authSchemaId !== authSchemaId) {
      setAuthSchemaId(props.task.authSchemaId);
    }
    calcIsReadonlyAssignToMe();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.task]);

  useEffect(() => {
    getAuthSchemaUsers(authSchemaId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authSchemaId]);

  //
  // Load
  //
  const calcIsReadonlyAssignToMe = async () => {
    // When disabled, Assign to me button is read-only, because the user cannot update the task at all
    if (props.disabled) {
      setIsReadonlyAssignToMe(true);

      return;
    }

    if (props.task.authSchemaId === undefined) {
      // When the current user and the current assignee of the task share a group, the user should be able to assign the task to himself
      // This applies on tasks without auth schema, because the auth schema takes precedence over group membership
      const groupMembers = await appContext.globalDataCache.groups.getCurrentUserMembers();
      const currentTaskUserIsMemberOfGroupsOfCurrentUser =
        groupMembers?.some((gm) => gm.members.includes(props.task.userId ?? '')) ?? false;

      if (currentTaskUserIsMemberOfGroupsOfCurrentUser) {
        setIsReadonlyAssignToMe(false);

        return;
      }
    }

    // When the user has update permissions on the task, the user should be able to assign the task to himself
    const canUpdateTask = canUpdateTaskField(props.orgTask, canUpdate, TaskFieldTypes.AssignToMe, appContext);
    setIsReadonlyAssignToMe(!canUpdateTask);
  };

  const getAuthSchemaUsers = async (authSchemaId: number | undefined): Promise<User[]> => {
    if (authSchemaId) {
      let usersInSchema: string[] = [];
      try {
        setAuthSchemaUsersLoading(true);
        const accessToken = await appContext.getAccessToken(apiRequest.scopes);
        const lines = await apiGetAuthSchemaLines(authSchemaId, accessToken);
        usersInSchema = lines.map((l) => l.userId) as string[];
      } catch (err) {
        appContext.setError(err);
        usersInSchema = [];
      } finally {
        setAuthSchemaUsersLoading(false);
      }

      if (props.task.userId && !usersInSchema.includes(props.task.userId)) {
        //it can be the case that the assignee of the task has been removed from the auth schema
        //to show the assignee, add it to the allowed list for the user picker
        usersInSchema.push(props.task.userId);
      }

      const users = appContext.globalDataCache.users.getItemsForId(usersInSchema);
      setAuthSchemaUsers(users);

      return users;
    } else {
      setAuthSchemaUsers([]);
    }

    return [];
  };

  const getSchemaPicker = () => {
    if (!canShowTaskSchema(appContext)) return null;
    if (isReadonlyPermissions && isReadonlyAssignee) {
      return (
        <IPStack.Item
          styles={!isReadonlyAssignee ? { root: { width: '50%', paddingBottom: 10 } } : { root: { width: '50%' } }}
        >
          <IPLabel>{t('translation:BulkActionsModal.Permissions.LabelSet')}</IPLabel>
          <IPText>{getTaskSchemaName(props.task, appContext, t)}</IPText>
        </IPStack.Item>
      );
    } else {
      return (
        <IPStack.Item grow={1}>
          <AuthSchemaPicker
            label={t('translation:BulkActionsModal.Permissions.LabelSet')}
            selectedAuthSchemaId={authSchemaId}
            onSelect={(authSchema) => {
              const copyTask = props.task.clone();
              copyTask.authSchemaId = authSchema?.authSchemaId;
              props.onUpdate(copyTask);
            }}
            hideMembersOnHover={true}
            disabled={isReadonlyPermissions}
            showForUserId={props.task.userId}
            isOwner={props.task.ownerId === appContext.user.id}
            overrideAdmin={!isReadonlyPermissions} //not only admin can set auth schemas for tasks
          />
          {props.task.taskId === -1 && props.task.authSchemaId === undefined && (
            <IPText variant="small" styles={{ root: { color: theme.palette.neutralSecondary } }}>
              {t('task:Assignment.SharedWithEveryoneHint')}
            </IPText>
          )}
        </IPStack.Item>
      );
    }
  };

  const getAssignToMe = () => {
    return (
      <IPStack.Item>
        <IPTooltipHost content={t('task:Assignment.AssignMeInfo')}>
          <IPIconButton
            className="isoplanner-task-assigntome"
            iconProps={{ iconName: 'UserFollowed' }}
            text=""
            onClick={() => {
              const copyTask = props.task.clone();
              copyTask.user = appContext.user;
              copyTask.userId = appContext.user.id;
              if (copyTask.authSchemaId) {
                if (!appContext.globalDataCache.authSchemas.get(copyTask.authSchemaId).hasLine(copyTask.userId)) {
                  copyTask.authSchemaId = undefined;
                }
              }
              props.onUpdate(copyTask);
            }}
            disabled={isReadonlyAssignToMe || authSchemaUsersLoading || appContext.user.id === props.task.userId}
          />
        </IPTooltipHost>
      </IPStack.Item>
    );
  };

  const getAssigneePicker = () => {
    if (isReadonlyAssignee) {
      const user = appContext.globalDataCache.users.get(props.task.userId);

      return (
        <IPStack horizontal tokens={globalStackTokensGapSmall} verticalAlign="end">
          <IPStack.Item>
            <IPLabel>{t('task:Assignment.Label')}</IPLabel>
            <IPPersona imageUrl={user.getPhotoURLObj()} size={IPPersonaSize.size24} text={user.name || '-'} />
          </IPStack.Item>
          {getAssignToMe()}
        </IPStack>
      );
    } else {
      return (
        <Fragment>
          <IPStack.Item grow={2}>
            <IPStack verticalFill>
              <IPLabel>{t('task:Assignment.Label')}</IPLabel>
              <IPStack horizontal verticalAlign="center" tokens={rowTokens}>
                <IPStack.Item grow={1}>
                  <UserPicker
                    users={authSchemaUsers}
                    disabled={authSchemaUsersLoading}
                    selectedItemId={props.task.userId}
                    onSelect={(item: User | undefined) => {
                      const copyTask = props.task.clone();
                      copyTask.userId = undefined;
                      copyTask.user = undefined;
                      if (item) {
                        const user = item;
                        copyTask.userId = user.id;
                        copyTask.user = user;
                      }
                      props.onUpdate(copyTask);
                    }}
                  />
                </IPStack.Item>
                {getAssignToMe()}
              </IPStack>
              {props.getErrorMessage && (
                <IPText variant="small" styles={{ root: { color: 'red' } }}>
                  {props.getErrorMessage('assignment')}
                </IPText>
              )}
            </IPStack>
          </IPStack.Item>
        </Fragment>
      );
    }
  };

  //
  // Main render
  //
  return (
    // top-aligned so the labels and inputs line up when a hint or error makes one column taller
    <IPStack horizontal verticalAlign="start" tokens={rowTokens}>
      {!props.hidePermissions && getSchemaPicker()}
      {!props.hideAssignee && getAssigneePicker()}
    </IPStack>
  );
};
